import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslate } from "../../locales/hooks";

interface PositionDetails {
  title: string;
  period?: string;
  duration?: string;
  responsibilities?: string[];
  achievements?: string[];
}

const PositionModal = ({
  open,
  details,
  onClose,
}: {
  open: boolean;
  details: PositionDetails | null;
  onClose: () => void;
}) => {
  const t = useTranslate();

  useEffect(() => {
    if (!open) return;

    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;

      e.preventDefault();
      e.stopPropagation();

      onClose();
    };

    window.addEventListener("keydown", onKeyDownCapture, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKeyDownCapture, {
        capture: true,
      });
  }, [open, onClose]);

  if (!open || !details) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* overlay */}
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      <div className="relative flex h-full w-full items-center justify-center p-4">
        <div className="relative w-[min(92vw,720px)] max-h-[calc(100dvh-2rem)] overflow-hidden rounded-2xl bg-white shadow-xl">
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-4">
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-slate-900">
                {details.title}
              </h3>
              {(details.period || details.duration) && (
                <p className="mt-1 text-xs text-slate-500">
                  {details.period ?? ""}
                  {details.period && details.duration ? " · " : ""}
                  {details.duration ?? ""}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="shrink-0 rounded-lg border border-slate-200 px-2 py-1 text-sm hover:bg-slate-50"
            >
              ✕
            </button>
          </div>

          {/* inside modal window */}
          <div className="max-h-[calc(100dvh-2rem-56px-56px)] overflow-y-auto p-4">
            <div className="max-h-[70vh] overflow-y-auto p-4">
              {details.responsibilities?.length ? (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-slate-900">
                    {t("miniCv__responsibilities")}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
                    {details.responsibilities.map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {details.achievements?.length ? (
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {t("miniCv__achievements")}
                  </p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
                    {details.achievements.map((x, i) => (
                      <li key={i}>{x}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-slate-500">{t("miniCv__noDetails")}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end border-t border-slate-200 p-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              {t("miniCv__close")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PositionModal;
export type { PositionDetails };
